const isPlainObject = (v) =>
  Object.prototype.toString.call(v) === "[object Object]";

export const deepClone = (value, seen = new WeakMap()) => {
  if (Object(value) !== value) return value;            // primitive Werte
  if (seen.has(value)) return seen.get(value);          // Zyklen

  let out;
  if (Array.isArray(value)) {
    out = [];
    seen.set(value, out);
    for (const v of value) out.push(deepClone(v, seen));
    return out;
  }
  if (value instanceof Date)     return new Date(value.getTime());
  if (value instanceof RegExp)   return new RegExp(value.source, value.flags);
  if (value instanceof Set) {
    out = new Set();
    seen.set(value, out);
    for (const v of value) out.add(deepClone(v, seen));
    return out;
  }
  if (value instanceof Map) {
    out = new Map();
    seen.set(value, out);
    for (const [k, v] of value) out.set(k, deepClone(v, seen));
    return out;
  }

  out = Object.create(Object.getPrototypeOf(value));
  seen.set(value, out);
  for (const key of Reflect.ownKeys(value)) {
    out[key] = deepClone(value[key], seen);
  }
  return out;
}

/**
 * Immutable Deep-Merge:
 * - Plain Objects werden rekursiv zusammengeführt
 * - Arrays / Sets / Maps werden durch source ersetzt (cloned)
 * - Mismatched Types: source gewinnt
 * - obj1 und obj2 bleiben unverändert; Ergebnis nutzt nur neue Instanzen
 */
export const deepMergeImmutable = (obj1, obj2, seen = new WeakMap()) => {
  // Falls obj2 undefiniert ist, clone von obj1
  if (obj2 === undefined) return deepClone(obj1);

  const isObj1 = Object(obj1) === obj1;
  const isObj2 = Object(obj2) === obj2;
  if (!isObj2) return deepClone(obj2);
  if (!isObj1) return deepClone(obj2);

  if (Array.isArray(obj2)) return deepClone(obj2);
  if (obj2 instanceof Set)  return deepClone(obj2);
  if (obj2 instanceof Map)  return deepClone(obj2);
  if (obj2 instanceof Date || obj2 instanceof RegExp) return deepClone(obj2);

  const result = Object.create(Object.getPrototypeOf(obj1) || Object.prototype);

  const keys = new Set([...Reflect.ownKeys(obj1), ...Reflect.ownKeys(obj2)]);
  for (const key of keys) {
    const v1 = obj1[key];
    const v2 = obj2[key];

    if (v2 === undefined) {
      result[key] = deepClone(v1);
      continue;
    }

    if (isPlainObject(v1) && isPlainObject(v2)) {
      result[key] = deepMergeImmutable(v1, v2, seen);
    } else if (isPlainObject(v2)) {
      result[key] = deepClone(v2);
    } else if (Array.isArray(v2) || v2 instanceof Set || v2 instanceof Map ||
      v2 instanceof Date || v2 instanceof RegExp ||
      Object(v2) !== v2) {
      result[key] = deepClone(v2);
    } else {
      result[key] = deepClone(v2);
    }
  }

  return result;
}
